import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { Group, Expense, Balance, Settlement } from '../types';
import { logger } from '../utils/logger';
import { useWebSocket } from './WebSocketContext';

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

// Types for cached data
interface CachedData<T> {
  data: T;
  timestamp: number;
}

interface AppContextType {
  // Cached data
  groups: Group[];
  balances: { [groupId: string]: Balance[] };
  expenses: { [groupId: string]: Expense[] };
  settlements: Settlement[];
  
  // Loading states
  groupsLoading: boolean;
  balancesLoading: { [groupId: string]: boolean };
  expensesLoading: { [groupId: string]: boolean };
  settlementsLoading: boolean;
  
  // Fetch functions (with caching)
  fetchGroups: () => Promise<Group[]>;
  fetchBalances: (groupId: string) => Promise<Balance[]>;
  fetchExpenses: (groupId: string) => Promise<Expense[]>;
  fetchSettlements: (userId: string) => Promise<Settlement[]>;
  
  // Optimistic updates (update UI immediately, sync with API in background)
  addExpenseOptimistically: (groupId: string, expense: Expense) => Promise<void>;
  updateExpenseOptimistically: (groupId: string, expenseId: string, updates: Partial<Expense>) => Promise<void>;
  deleteExpenseOptimistically: (groupId: string, expenseId: string) => Promise<void>;
  addSettlementOptimistically: (settlement: Settlement) => Promise<void>;
  
  // Cache invalidation
  invalidateGroups: () => void;
  invalidateBalances: (groupId: string) => void;
  invalidateExpenses: (groupId: string) => void;
  invalidateSettlements: () => void;
  invalidateAll: () => void;
  
  // Refresh trigger - incrementing this causes components to re-render
  refreshTrigger: number;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get WebSocket connection
  const { socket } = useWebSocket();
  
  // Cached data state
  const [groupsCache, setGroupsCache] = useState<CachedData<Group[]> | null>(null);
  const [balancesCache, setBalancesCache] = useState<{ [groupId: string]: CachedData<Balance[]> }>({});
  const [expensesCache, setExpensesCache] = useState<{ [groupId: string]: CachedData<Expense[]> }>({});
  const [settlementsCache, setSettlementsCache] = useState<CachedData<Settlement[]> | null>(null);
  
  // Loading states
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState<{ [groupId: string]: boolean }>({});
  const [expensesLoading, setExpensesLoading] = useState<{ [groupId: string]: boolean }>({});
  const [settlementsLoading, setSettlementsLoading] = useState(false);
  
  // Refresh trigger - incrementing this causes all components to refetch
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Helper: Check if cache is still valid
  const isCacheValid = (cached: CachedData<any> | null): boolean => {
    if (!cached) return false;
    return Date.now() - cached.timestamp < CACHE_TTL;
  };

  // Fetch groups with caching
  const fetchGroups = useCallback(async (): Promise<Group[]> => {
    // Return cached data if valid
    if (isCacheValid(groupsCache)) {
      logger.debug('📦 Using cached groups');
      return groupsCache!.data;
    }

    // Fetch fresh data
    logger.debug('🌐 Fetching fresh groups');
    setGroupsLoading(true);
    try {
      const groups = await apiService.getGroups();
      
      setGroupsCache({
        data: groups,
        timestamp: Date.now()
      });
      
      return groups;
    } catch (error) {
      logger.error('Failed to fetch groups:', error);
      return groupsCache?.data || [];
    } finally {
      setGroupsLoading(false);
    }
  }, [groupsCache]);

  // Fetch balances with caching
  const fetchBalances = useCallback(async (groupId: string): Promise<Balance[]> => {
    // Return cached data if valid
    const cached = balancesCache[groupId];
    if (isCacheValid(cached)) {
      logger.debug(`📦 Using cached balances for group ${groupId}`);
      return cached!.data;
    }

    // Fetch fresh data
    logger.debug(`🌐 Fetching fresh balances for group ${groupId}`);
    setBalancesLoading(prev => ({ ...prev, [groupId]: true }));
    try {
      const balances = await apiService.getGroupBalances(groupId);
      
      setBalancesCache(prev => ({
        ...prev,
        [groupId]: {
          data: balances,
          timestamp: Date.now()
        }
      }));
      
      return balances;
    } catch (error) {
      logger.error(`Failed to fetch balances for group ${groupId}:`, error);
      return cached?.data || [];
    } finally {
      setBalancesLoading(prev => ({ ...prev, [groupId]: false }));
    }
  }, [balancesCache]);

  // Fetch expenses with caching
  const fetchExpenses = useCallback(async (groupId: string): Promise<Expense[]> => {
    // Return cached data if valid
    const cached = expensesCache[groupId];
    if (isCacheValid(cached)) {
      logger.debug(`📦 Using cached expenses for group ${groupId}`);
      return cached!.data;
    }

    // Fetch fresh data
    logger.debug(`🌐 Fetching fresh expenses for group ${groupId}`);
    setExpensesLoading(prev => ({ ...prev, [groupId]: true }));
    try {
      const expenses = await apiService.getExpenses(groupId);
      
      setExpensesCache(prev => ({
        ...prev,
        [groupId]: {
          data: expenses,
          timestamp: Date.now()
        }
      }));
      
      return expenses;
    } catch (error) {
      logger.error(`Failed to fetch expenses for group ${groupId}:`, error);
      return cached?.data || [];
    } finally {
      setExpensesLoading(prev => ({ ...prev, [groupId]: false }));
    }
  }, [expensesCache]);

  // Fetch settlements with caching
  const fetchSettlements = useCallback(async (userId: string): Promise<Settlement[]> => {
    // Return cached data if valid
    if (isCacheValid(settlementsCache)) {
      logger.debug('📦 Using cached settlements');
      return settlementsCache!.data;
    }

    // Fetch fresh data
    logger.debug('🌐 Fetching fresh settlements');
    setSettlementsLoading(true);
    try {
      // Fetch both completed and verifying settlements
      const [completedSettlements, verifyingSettlements] = await Promise.all([
        apiService.getSettlements(userId, 'completed'),
        apiService.getSettlements(userId, 'verifying')
      ]);
      
      const settlements = [...completedSettlements, ...verifyingSettlements];
      
      setSettlementsCache({
        data: settlements,
        timestamp: Date.now()
      });
      
      return settlements;
    } catch (error) {
      logger.error('Failed to fetch settlements:', error);
      return settlementsCache?.data || [];
    } finally {
      setSettlementsLoading(false);
    }
  }, [settlementsCache]);

  // ============================================
  // OPTIMISTIC UPDATE FUNCTIONS
  // These update the UI immediately, then sync with API
  // If API fails, we revert the optimistic update
  // ============================================

  /**
   * Add expense optimistically
   * 1. Add to local cache immediately (user sees it right away)
   * 2. Sync with API in background
   * 3. If API fails, remove from cache and show error
   */
  const addExpenseOptimistically = useCallback(async (groupId: string, expense: Expense): Promise<void> => {
    logger.debug(`⚡ Optimistic: Adding expense ${expense.id} to group ${groupId}`);
    
    // 1. Add to cache immediately
    setExpensesCache(prev => {
      const cached = prev[groupId];
      if (!cached) return prev;
      
      return {
        ...prev,
        [groupId]: {
          data: [expense, ...cached.data], // Add to beginning
          timestamp: Date.now()
        }
      };
    });
    
    // 2. Invalidate balances (will be recalculated)
    invalidateBalances(groupId);
    
    // 3. Trigger refresh
    setRefreshTrigger(prev => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Update expense optimistically
   */
  const updateExpenseOptimistically = useCallback(async (
    groupId: string,
    expenseId: string,
    updates: Partial<Expense>
  ): Promise<void> => {
    logger.debug(`⚡ Optimistic: Updating expense ${expenseId}`);
    
    // Update in cache immediately
    setExpensesCache(prev => {
      const cached = prev[groupId];
      if (!cached) return prev;
      
      return {
        ...prev,
        [groupId]: {
          data: cached.data.map(exp => 
            exp.id === expenseId ? { ...exp, ...updates } : exp
          ),
          timestamp: Date.now()
        }
      };
    });
    
    // Invalidate balances
    invalidateBalances(groupId);
    setRefreshTrigger(prev => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Delete expense optimistically
   */
  const deleteExpenseOptimistically = useCallback(async (groupId: string, expenseId: string): Promise<void> => {
    logger.debug(`⚡ Optimistic: Deleting expense ${expenseId}`);
    
    // Remove from cache immediately
    setExpensesCache(prev => {
      const cached = prev[groupId];
      if (!cached) return prev;
      
      return {
        ...prev,
        [groupId]: {
          data: cached.data.filter(exp => exp.id !== expenseId),
          timestamp: Date.now()
        }
      };
    });
    
    // Invalidate balances
    invalidateBalances(groupId);
    setRefreshTrigger(prev => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Add settlement optimistically
   */
  const addSettlementOptimistically = useCallback(async (settlement: Settlement): Promise<void> => {
    logger.debug(`⚡ Optimistic: Adding settlement ${settlement.id}`);
    
    // Add to cache immediately
    setSettlementsCache(prev => {
      if (!prev) return prev;
      
      return {
        data: [settlement, ...prev.data],
        timestamp: Date.now()
      };
    });
    
    // Invalidate all balances (settlements affect multiple groups)
    setBalancesCache({});
    
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Cache invalidation functions
  const invalidateGroups = useCallback(() => {
    logger.debug('🗑️  Invalidating groups cache');
    setGroupsCache(null);
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const invalidateBalances = useCallback((groupId: string) => {
    logger.debug(`🗑️  Invalidating balances cache for group ${groupId}`);
    setBalancesCache(prev => {
      const newCache = { ...prev };
      delete newCache[groupId];
      return newCache;
    });
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const invalidateExpenses = useCallback((groupId: string) => {
    logger.debug(`🗑️  Invalidating expenses cache for group ${groupId}`);
    setExpensesCache(prev => {
      const newCache = { ...prev };
      delete newCache[groupId];
      return newCache;
    });
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const invalidateSettlements = useCallback(() => {
    logger.debug('🗑️  Invalidating settlements cache');
    setSettlementsCache(null);
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const invalidateAll = useCallback(() => {
    logger.debug('🗑️  Invalidating ALL caches');
    setGroupsCache(null);
    setBalancesCache({});
    setExpensesCache({});
    setSettlementsCache(null);
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // ============================================
  // WEBSOCKET EVENT LISTENERS
  // Listen for real-time updates from other users
  // ============================================
  useEffect(() => {
    if (!socket) return;

    logger.info('🔌 Setting up WebSocket event listeners');

    // ⚡ Real-time: Expense created
    socket.on('expense:created', ({ groupId, expense }) => {
      logger.info(`⚡ Real-time: Expense created in group ${groupId}`, expense);
      
      // Add to cache immediately
      setExpensesCache(prev => {
        const cached = prev[groupId];
        if (!cached) return prev;
        
        // Check if expense already exists (avoid duplicates)
        const exists = cached.data.some(exp => exp.id === expense.id);
        if (exists) return prev;
        
        return {
          ...prev,
          [groupId]: {
            data: [expense, ...cached.data],
            timestamp: Date.now()
          }
        };
      });
      
      // Invalidate balances (will be recalculated)
      setBalancesCache(prev => {
        const newCache = { ...prev };
        delete newCache[groupId];
        return newCache;
      });
      
      setRefreshTrigger(prev => prev + 1);
    });

    // ⚡ Real-time: Expense updated
    socket.on('expense:updated', ({ groupId, expense }) => {
      logger.info(`⚡ Real-time: Expense updated in group ${groupId}`, expense);
      
      setExpensesCache(prev => {
        const cached = prev[groupId];
        if (!cached) return prev;
        
        return {
          ...prev,
          [groupId]: {
            data: cached.data.map(exp => 
              exp.id === expense.id ? expense : exp
            ),
            timestamp: Date.now()
          }
        };
      });
      
      // Invalidate balances
      setBalancesCache(prev => {
        const newCache = { ...prev };
        delete newCache[groupId];
        return newCache;
      });
      
      setRefreshTrigger(prev => prev + 1);
    });

    // ⚡ Real-time: Expense deleted
    socket.on('expense:deleted', ({ groupId, expenseId }) => {
      logger.info(`⚡ Real-time: Expense deleted in group ${groupId}`, expenseId);
      
      setExpensesCache(prev => {
        const cached = prev[groupId];
        if (!cached) return prev;
        
        return {
          ...prev,
          [groupId]: {
            data: cached.data.filter(exp => exp.id !== expenseId),
            timestamp: Date.now()
          }
        };
      });
      
      // Invalidate balances
      setBalancesCache(prev => {
        const newCache = { ...prev };
        delete newCache[groupId];
        return newCache;
      });
      
      setRefreshTrigger(prev => prev + 1);
    });

    // ⚡ Real-time: Settlement created
    socket.on('settlement:created', (settlement) => {
      logger.info(`⚡ Real-time: Settlement created`, settlement);
      
      setSettlementsCache(prev => {
        if (!prev) return prev;
        
        // Check if settlement already exists
        const exists = prev.data.some(s => s.id === settlement.id);
        if (exists) return prev;
        
        return {
          data: [settlement, ...prev.data],
          timestamp: Date.now()
        };
      });
      
      // Invalidate all balances (settlements affect multiple groups)
      setBalancesCache({});
      
      setRefreshTrigger(prev => prev + 1);
    });

    // ⚡ Real-time: Balance updated
    socket.on('balance:updated', ({ groupId }) => {
      logger.info(`⚡ Real-time: Balance updated in group ${groupId}`);
      
      // Invalidate balance cache for this group
      setBalancesCache(prev => {
        const newCache = { ...prev };
        delete newCache[groupId];
        return newCache;
      });
      
      setRefreshTrigger(prev => prev + 1);
    });

    // Cleanup listeners on unmount
    return () => {
      logger.info('🔌 Cleaning up WebSocket event listeners');
      socket.off('expense:created');
      socket.off('expense:updated');
      socket.off('expense:deleted');
      socket.off('settlement:created');
      socket.off('balance:updated');
    };
  }, [socket]);

  // Provide current data (for components that just want to read)
  const currentGroups = groupsCache?.data || [];
  const currentBalances = Object.fromEntries(
    Object.entries(balancesCache).map(([groupId, cached]) => [groupId, cached.data])
  );
  const currentExpenses = Object.fromEntries(
    Object.entries(expensesCache).map(([groupId, cached]) => [groupId, cached.data])
  );
  const currentSettlements = settlementsCache?.data || [];

  const value: AppContextType = {
    groups: currentGroups,
    balances: currentBalances,
    expenses: currentExpenses,
    settlements: currentSettlements,
    
    groupsLoading,
    balancesLoading,
    expensesLoading,
    settlementsLoading,
    
    fetchGroups,
    fetchBalances,
    fetchExpenses,
    fetchSettlements,
    
    addExpenseOptimistically,
    updateExpenseOptimistically,
    deleteExpenseOptimistically,
    addSettlementOptimistically,
    
    invalidateGroups,
    invalidateBalances,
    invalidateExpenses,
    invalidateSettlements,
    invalidateAll,
    
    refreshTrigger,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;

